import { RouterPrompt } from '@data-loss/components';
import { FC, PropsWithChildren, useState } from 'react';
import { DirtyContext } from './dirty-context';

/**
 * DirtyProvider is a functional component designed to manage the "dirty state" of a form or other interactive content.
 * It provides a context to its children components to track changes, warning users about unsaved changes when navigating away.
 *
 * This component leverages React context to pass down the `isDirty` state and the `setDirty` method to its descendants.
 * It uses `React.useState` to maintain the state of whether changes have been made (`isDirty`), and renders a prompt if there are unsaved changes.
 *
 * Props:
 * - children: ReactNode - The child components to be rendered within this provider.
 */
export const DirtyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isDirty, setDirty] = useState(false);

  return (
    <DirtyContext.Provider
      value={{
        isDirty,
        setDirty,
      }}
    >
      <RouterPrompt when={isDirty} />
      {children}
    </DirtyContext.Provider>
  );
};
